import React, { useState, useEffect } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  CheckSquare,
  X,
  MonitorPlay,
} from 'lucide-react';
import { Button } from '../UI/button';
import useIsMobile from '../../Hooks/useIsMobile';
import ShowImage from '../Common/ShowImage';
import { cn } from "../../Lib/Cn";

const QuickTraining = ({ trainingData: propData, slides, isOpen: externalIsOpen, onClose = () => { } }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;
  const trainingData = propData || slides;

  const [currentStep, setCurrentStep] = useState(-1);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const isMobile = useIsMobile();

  const isIntroductionStep = currentStep === -1;
  const isLastStep = currentStep === trainingData.steps.length - 1;
  const currentStepData = currentStep === -1 ? null : trainingData.steps[currentStep];

  const handleNext = () => {
    if (currentStep === -1) {
      // Moving from introduction to first step
      setCurrentStep(0);
    } else if (currentStep < trainingData.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep === 0) {
      // Moving from first step back to introduction
      setCurrentStep(-1);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    if (!isControlled) setInternalIsOpen(false);
    setCurrentStep(-1);
    setImageModalOpen(false);
    setSelectedImage(null);
    onClose();
  };

  const handleImageClick = (image) => {
    if (image) {
      setSelectedImage(image);
      setImageModalOpen(true);
    }
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  const [maxContentHeight, setMaxContentHeight] = useState(0);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = '';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && trainingData.steps) {
      // Create a temporary container to measure all steps
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.visibility = 'hidden';
      // Match the actual modal width on desktop (md:max-w-md = 448px)
      tempContainer.style.width = isMobile ? '90vw' : '32rem';
      tempContainer.style.padding = '1.25rem';
      tempContainer.style.fontSize = '1rem';
      tempContainer.style.lineHeight = '1.5';
      document.body.appendChild(tempContainer);

      let maxHeight = 0;

      // Measure introduction step
      const introDiv = document.createElement('div');
      introDiv.className = 'text-center space-y-4';
      introDiv.innerHTML = `
        <div style="width: 56px; height: 56px; border-radius: 50%; background: var(--sahi-green); display: flex; align-items: center; justify-content: center; margin: 0 auto;"></div>
        <h2 style="font-size: 1.125rem; font-weight: 600; color: #111827; padding: 0 1.5rem;">${trainingData.introduction.title}</h2>
        <p style="font-size: 1rem; color: #4b5563; line-height: 1.5; padding: 0 0.5rem;">${trainingData.introduction.description}</p>
      `;
      tempContainer.appendChild(introDiv);
      maxHeight = Math.max(maxHeight, introDiv.scrollHeight);
      tempContainer.removeChild(introDiv);

      // Measure each step
      trainingData.steps.forEach((step) => {
        const stepDiv = document.createElement('div');
        stepDiv.className = 'space-y-4';
        let pointsHtml = `
          <div style="display: flex; justify-content: center;">
            <div style="width: 56px; height: 56px; border-radius: 50%; background: var(--sahi-green); display: flex; align-items: center; justify-content: center;"></div>
          </div>
          <h2 style="font-size: 1.125rem; font-weight: 600; color: #111827; text-align: center; padding: 0 1rem;">${step.title}</h2>
          <div style="display: flex; flex-direction: column; gap: 0.75rem;">
        `;

        const showNumber = step.title !== "Customization" && step.title !== "Integrations";
        step.points.forEach((point, index) => {
          pointsHtml += `
            <div style="display: flex; gap: 0.75rem; align-items: flex-start;">
              ${showNumber ? `<div style="flex-shrink: 0; width: 20px; height: 20px; border-radius: 50%; background: var(--sahi-green); color: white; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 500; margin-top: 0.25rem;">${index + 1}</div>` : ''}
              <p style="font-size: 1rem; color: #374151; line-height: 1.5; flex: 1;">${point}</p>
            </div>
          `;
        });

        pointsHtml += '</div>';

        // Add integrations grid height if present
        if (step.integrations && step.integrations.length > 0) {
          pointsHtml += `<div style="margin-top: 1rem; display: flex; justify-content: center;">
            <div style="display: flex; flex-wrap: wrap; justify-content: center; gap: 0.5rem; max-width: fit-content;">
              ${step.integrations.map(() => `<div style="border-radius: 0.5rem; background: #f3f4f6; border: 1px solid #e5e7eb; width: 70px; height: 70px; display: flex; align-items: center; justify-content: center;"></div>`).join('')}
            </div>
          </div>`;
        }

        stepDiv.innerHTML = pointsHtml;
        tempContainer.appendChild(stepDiv);
        maxHeight = Math.max(maxHeight, stepDiv.scrollHeight);
        tempContainer.removeChild(stepDiv);
      });

      document.body.removeChild(tempContainer);
      if (maxHeight > 0) {
        setMaxContentHeight(maxHeight);
      }
    }
  }, [isOpen, trainingData, isMobile]);

  return (
    <>
      {/* Button - Always visible if not controlled */}
      {!isControlled && (
        <Button
          onClick={() => setInternalIsOpen(true)}
          // Mobile stays sahiGreen, Desktop uses outline to allow custom white bg/border
          variant={isMobile ? "sahiGreen" : "outline"}
          className={cn(
            isMobile
              ? "w-full h-10 text-sm px-3 flex-1 sm:w-auto sm:flex-none"
              : "bg-white text-sahi-dark-blue border-sahi-dark-blue hover:bg-sahi-dark-blue hover:text-white h-10 text-sm px-3 flex-1 sm:flex-none transition-colors"
          )}
          size={isMobile ? "default" : "lg"}
        >
          <MonitorPlay className= {isMobile ? "h-5 w-5 mr-2" : "h-3.5 w-3.5 mr-1" } />
          <span className={isMobile ? "text-sm" : "text-[14px]"}>Training</span>
        </Button>
      )}
      {/* Training Modal - Only shows when isOpen is true */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={handleClose}
          />

          {/* Training Popover */}
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div
              className="bg-white rounded-lg shadow-2xl w-[90vw] md:w-auto md:max-w-md relative"
              onClick={(e) => e.stopPropagation()}
              style={{
                animation: 'fadeIn 0.2s ease-out'
              }}
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors z-10"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Content Area */}
              <div
                className="p-5 md:p-6 flex flex-col justify-center items-center"
                style={{ minHeight: maxContentHeight > 0 ? `${maxContentHeight}px` : 'auto' }}
              >
                <div
                  data-training-content
                  className="w-full flex flex-col justify-center items-center"
                >
                  {/* Introduction Step */}
                  {isIntroductionStep ? (
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-12 h-12 md:w-14 md:h-14 bg-sahi-green rounded-full flex items-center justify-center">
                        <CheckSquare className="h-6 w-6 md:h-7 md:w-7 text-sahi-white" />
                      </div>
                      <h2 className="text-lg md:text-xl font-semibold text-gray-900 px-6">
                        {trainingData.introduction.title}
                      </h2>
                      <p
                        className="text-sm md:text-base text-gray-600 leading-relaxed px-2"
                        dangerouslySetInnerHTML={{ __html: trainingData.introduction.description }}
                      />
                    </div>
                  ) : (
                    /* Step Content */
                    <div className="space-y-4 w-full">
                      {/* Icon */}
                      <div className="flex justify-center">
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-sahi-green rounded-full flex items-center justify-center">
                          {React.cloneElement(currentStepData.icon, {
                            className: "h-6 w-6 md:h-7 md:w-7 text-sahi-white"
                          })}
                        </div>
                      </div>

                      {/* Title */}
                      <h2 className="text-lg md:text-xl font-semibold text-gray-900 text-center px-4">
                        {currentStepData.title}
                      </h2>

                      {/* Points */}
                      <div className="space-y-3">
                        {currentStepData && currentStepData.points.map((point, index) => {
                          const showNumber = currentStepData.title !== "Customization" && currentStepData.title !== "Integrations";
                          const isCentered = currentStepData.title === "Customization";
                          return (
                            <div key={index} className={`flex gap-3 items-start ${!showNumber ? 'pl-2' : ''} ${isCentered ? 'justify-center' : ''}`}>
                              {showNumber && (
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-sahi-green text-white flex items-center justify-center text-xs font-medium mt-1">
                                  {index + 1}
                                </div>
                              )}
                              <p
                                className={`text-sm md:text-base text-gray-700 leading-relaxed flex-1 ${isCentered ? 'text-center' : ''}`}
                                dangerouslySetInnerHTML={{ __html: point }}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* Additional Image/Content if provided */}
                      {(isMobile ? currentStepData.mobileImage : currentStepData.desktopImage) && (
                        <div className="mt-4">
                          {typeof (isMobile ? currentStepData.mobileImage : currentStepData.desktopImage) === 'string' && ((isMobile ? currentStepData.mobileImage : currentStepData.desktopImage).startsWith('http') || (isMobile ? currentStepData.mobileImage : currentStepData.desktopImage).includes('.png') || (isMobile ? currentStepData.mobileImage : currentStepData.desktopImage).includes('.jpg') || (isMobile ? currentStepData.mobileImage : currentStepData.desktopImage).includes('.jpeg')) ? (
                            <div
                              className="rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => handleImageClick(isMobile ? currentStepData.mobileImage : currentStepData.desktopImage)}
                            >
                              <img
                                src={isMobile ? currentStepData.mobileImage : currentStepData.desktopImage}
                                alt={currentStepData.title}
                                className="w-full h-auto object-contain max-h-[35vh]"
                              />
                            </div>
                          ) : (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs md:text-sm text-gray-600 text-center italic">
                                {isMobile ? currentStepData.mobileImage : currentStepData.desktopImage}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Integrations Grid if provided */}
                      {currentStepData.integrations && currentStepData.integrations.length > 0 && (
                        <div className="mt-4 flex justify-center">
                          <div className="flex flex-wrap justify-center gap-2" style={{ maxWidth: 'fit-content' }}>
                            {currentStepData.integrations.map((item) => (
                              <div
                                key={item.id}
                                className="flex flex-col items-center justify-center rounded-lg bg-gray-100 border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                style={{ width: '70px', height: '70px' }}
                              >
                                <img
                                  src={item.image}
                                  alt={item.alt}
                                  className="h-14 w-14 object-contain"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer with Navigation */}
              <div className="border-t border-gray-200 px-5 py-4 md:px-6 bg-gray-50 rounded-b-lg">
                <div className="flex items-center justify-between gap-3">
                  {/* Progress Indicator */}
                  <div className="text-xs md:text-sm text-gray-500 font-medium">
                    {isIntroductionStep ? '0' : currentStep + 1}/{trainingData.steps.length}
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex gap-2">
                    {!isIntroductionStep && (
                      <Button
                        onClick={handlePrevious}
                        variant="outline"
                        className="gap-1.5 text-sm"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="sm:inline">Previous</span>
                      </Button>
                    )}

                    <Button
                      onClick={handleNext}
                      variant="sahiGreen"
                      className="gap-1.5 text-sm px-4"
                    >
                      {isLastStep ? 'Got it' : 'Next'}
                      {!isLastStep && <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
        </>
      )}

      {/* Show Image Modal */}
      <ShowImage
        isOpen={imageModalOpen}
        imageUrl={selectedImage}
        altText={currentStepData?.title || "Training image"}
        onClose={handleCloseImageModal}
      />
    </>
  );
};

export default QuickTraining;